import React, { useEffect, useRef, useState } from 'react';
import {
  TextInput,
  TextArea,
  Button,
  RadioButtonGroup,
  RadioButton,
  DatePicker,
  DatePickerInput,
  Dropdown,
  NumberInput,
  Link,
  TextInputSkeleton,
  Checkbox,
  Loading
} from 'carbon-components-react';
import dayjs from 'dayjs';
import { referralChannelAdvancedOptions, Options, OptionItem } from 'types';
import { FormError } from 'components';
import { Add16, WarningFilled16 } from '@carbon/icons-react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectSelectedAuthorisation,
  selectAuthorisationProcess,
  selectAuthorisationOrderProcess,
  selectAuthorisationTypesOption,
  selectAuthorisationError
} from 'redux/authorisation/selectors';
import ExtendedToastNotification from '../CustomToastNotification';
import {
  addAuthorisationStart,
  addAuthorisationOrderStart,
  deleteAuthorisationOrderStart,
  deleteAuthorisationStart,
  updateAuthorisationStart,
  clearSelectedAuthorisation,
  clearError
} from 'redux/authorisation/actions';
import AuthorisationFormItem from './formItem';

type AuthorisationType = {
  value: string;
  label: string;
} | null;
type Props = {
  formType: 'add' | 'edit' | 'add-order' | undefined;
  authorisationId?: number;
  onClose: Function;
  advancedOptions: React.MutableRefObject<
    referralChannelAdvancedOptions | undefined
  >;
  setFormType: (formType: 'add' | 'edit' | 'add-order' | undefined) => void;
};
const channelOptionItems: Options = [
  {
    label: 'BYDA Direct',
    value: 'BYDA Direct'
  },
  {
    label: 'Reseller (PelicanCorp)',
    value: 'Reseller (PelicanCorp)'
  },
  {
    label: 'Self Service',
    value: 'Self Service'
  },
  {
    label: 'Other',
    value: 'Other'
  }
];
const AuthorisationForm: React.FC<Props> = ({
  formType,
  authorisationId,
  onClose,
  setFormType
}) => {
  const categoryListItems = useSelector(selectAuthorisationTypesOption) || [];
  const authorisationDetails = useSelector(selectSelectedAuthorisation);
  const retrieveAuthorisationProcess = useSelector(selectAuthorisationProcess);
  const retrieveOrderProcessStatus = useSelector(
    selectAuthorisationOrderProcess
  );
  const retrieveAuthorisationError = useSelector(selectAuthorisationError);

  const dispatch: any = useDispatch();
  // main input fields
  const [firstName, setFirstName] = useState('');
  const [channel, setChannel] = useState<OptionItem | null>(null);
  const [channelFreeText, setChannelFreeText] = useState('');
  const [primaryContactEmail, setPrimaryContactEmail] = useState('');
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [note, setNote] = useState('');
  const [isBydaMember, setIsBydaMember] = useState(true);
  const [isMembershipAllowance, setIsMembershipAllowance] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [authorisation, setAuthorisation] = useState<AuthorisationType>(null);
  const [externalId, setExternalId] = useState('');
  const [radioValue, setRadioValue] = useState<string>('add-specific-users');
  const [onlyEmailAddress, setOnlyEmailAddress] = useState<string>('');
  const [onlyDomain, setOnlyDomain] = useState<string>('');
  const [transactions, setTransactions] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<Date[]>([]);
  const [orderReferenceCode, setOrderReferenceCode] = useState('');
  const [authorisationIdToDelete, setAuthorisationIdToDelete] = useState<
    number | null
  >(null);
  const [orderIdToDelete, setOrderIdToDelete] = useState<number | null>(null);
  // show add order ui
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showAddOrderLink, setShowAddOrderLink] = useState(true);
  const [showAllOrders, setShowAllOrders] = useState(false);
  // show toast
  const [showToast, setShowToast] = useState(false);
  const [showDeleteToast, setShowDeleteToast] = useState(false);
  // unitial loading state
  const [isLoading, setIsLoading] = useState(true);
  const [editCloseButton, setEditCloseButton] = useState(false);

  const [isAddingOrder, setIsAddingOrder] = useState(false);
  const [isUpdatingAuthorisation, setIsUpdatingAuthorisation] = useState(false);
  const [isCreatingAuthorisation, setIsCreatingAuthorisation] = useState(false);

  const [errorMatchEmailDomains, setErrorMatchEmailDomains] = useState(false);
  const [errorAddSpecificUsers, setErrorAddSpecificUsers] = useState(false);
  const [disableEdit, setDisableEdit] = useState(false);

  const [clearField, setclearField] = useState(false);

  const [isAllFormDisabled, setIsAllFormDisabled] = useState(false);
  const [isDisableFormAfterCreate] = useState(false);

  const isOrderFormDisabled = retrieveOrderProcessStatus === 'adding';

  // for email validation
  const [isEmailValid, setIsEmailValid] = useState(true);

  const [isPrimaryContactEmailValid, setIsPrimaryContactEmailValid] = useState(
    true
  );

  const [ownerEmail, setOwnerEmail] = useState('');
  const [isOwnerEmailValid, setIsOwnerEmailValid] = useState(true);
  const [ownerEmailError, setOwnerEmailError] = useState<string>(
    'Invalid email'
  );

  // for domain validation
  const [isDomainValid, setIsDomainValid] = useState(true);

  // order transactions setting
  const MAX_AREA_STEP = 100;
  const MAX_AREA_MAX = 500000;
  const MAX_AREA_MIN = 0;

  // button validation
  const validationForEdit =
    firstName &&
    authorisation &&
    primaryContactEmail &&
    primaryContactName &&
    ((channel?.value === 'Other' && !!channelFreeText) ||
      (channel?.value !== 'Other' && !!channel?.value)) &&
    (externalId || !isBydaMember) &&
    ((radioValue === 'add-specific-users' && onlyEmailAddress) ||
      (radioValue === 'match-email-domains' && onlyDomain));

  const validationForAddOrder =
    endDate.length > 0 &&
    transactions &&
    transactions <= MAX_AREA_MAX &&
    orderReferenceCode;

  const validationForCreate = validationForEdit && validationForAddOrder;

  // form enable/ disable fields
  const isRadioButtonGroupEnabled = !!authorisation;
  // functions
  const handleNumberInputChange = (event: any) => {
    const newValue = Number(event.imaginaryTarget.value);
    if (!isNaN(newValue) && newValue > 0) {
      setTransactions(newValue);
    } else {
      setTransactions(0);
    }
  };
  const handleDatePickerChange = (dates: any[]) => {
    if (dates && dates.length > 0) {
      const selectedValueDate = dates[0];
      const selectedDate = dayjs(selectedValueDate).endOf('day').toDate();
      setEndDate([selectedDate]);
    } else {
      setEndDate([]);
    }
  };

  const handleRadioChange = () => {
    dispatch(clearError());
  };

  // convert date to dd/mm/yyyy
  const formatDateToDDMMYYYY = (date: string | Date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSubmit = () => {
    const formData = {
      name: firstName,
      channel:
        channel?.value === 'Other' ? channelFreeText : channel?.value || '',
      authorisationType: authorisation ? authorisation.value : undefined,
      externalId: isBydaMember ? externalId : null,
      primaryContactEmail: primaryContactEmail || null,
      primaryContactName: primaryContactName || null,
      ownerEmail: ownerEmail || null,
      note: note || null,
      domains:
        radioValue === 'match-email-domains'
          ? onlyDomain.split(',').map((s) => s.trim())
          : [],
      users:
        radioValue === 'add-specific-users'
          ? onlyEmailAddress.split(',').map((s) => s.trim())
          : [],
      orders: [
        {
          amount: transactions || 0,
          expiredAt: dayjs(endDate.length > 0 ? endDate[0] : new Date())
            .endOf('day')
            .toISOString(),
          note: orderNote || null,
          isMembershipAllowance: isMembershipAllowance || false,
          reference: orderReferenceCode
        }
      ]
    };
    dispatch(addAuthorisationStart(formData));
    setIsCreatingAuthorisation(true);
    setDisableEdit(false);
  };
  const handleCancel = () => {
    dispatch(clearSelectedAuthorisation());
    onClose();
  };
  const handleAddOrder = () => {
    const orderData = {
      id: Number(authorisationId),
      payload: {
        reference: orderReferenceCode,
        amount: transactions || 0,
        note: orderNote || null,
        isMembershipAllowance: isMembershipAllowance || false,
        expiredAt: dayjs(endDate.length > 0 ? endDate[0] : new Date())
          .endOf('day')
          .toISOString()
      }
    };
    dispatch(addAuthorisationOrderStart(orderData));
    setIsAddingOrder(true);
    setTransactions(0);
    setEndDate([]);
    setOrderReferenceCode('');
    setIsMembershipAllowance(false);
    setOrderNote('');
  };
  const handleDeleteOrder = (authorisationId: number, orderId: number) => {
    dispatch(deleteAuthorisationOrderStart(authorisationId, orderId));
  };
  const handleSuspendClick = () => {
    setShowToast(true);
    setShowDeleteToast(false);
  };
  const confirmSuspend = () => {
    if (authorisationId) {
      dispatch(deleteAuthorisationStart(authorisationId));
      setTimeout(() => {
        dispatch(clearSelectedAuthorisation());
        setShowToast(false);
        onClose();
      }, 500);
    }
  };
  // edit
  const handleUpdateAuthorisation = () => {
    const formData = {
      id: authorisationId || 0,
      payload: {
        name: firstName,
        channel:
          channel?.value === 'Other' ? channelFreeText : channel?.value || '',
        externalId: isBydaMember ? externalId : null,
        primaryContactEmail: primaryContactEmail || null,
        primaryContactName: primaryContactName || null,
        ownerEmail: ownerEmail || null,
        note: note || null,
        domains:
          radioValue === 'match-email-domains'
            ? onlyDomain.split(',').map((s) => s.trim())
            : [],
        users:
          radioValue === 'add-specific-users'
            ? onlyEmailAddress.split(',').map((s) => s.trim())
            : []
      }
    };
    dispatch(updateAuthorisationStart(formData));
    setIsUpdatingAuthorisation(true);
    setEditCloseButton(true);
    setDisableEdit(true);
  };

  // byda id number only
  const handleExternalIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (/^\d*$/.test(newValue)) {
      setExternalId(newValue);
    }
  };

  // mininum date
  const today = new Date();
  const minDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).toISOString();

  const handleOrderReferenceCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderReferenceCode(e.target.value);
  };

  // email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmailAddresses = (emailAddresses: string) => {
    const emails = emailAddresses.split(',');
    return emails.every(isValidEmail);
  };

  const handleEmailChange = (e: any) => {
    const emailValue = e.target.value;
    setOnlyEmailAddress(emailValue);

    if (emailValue) {
      setIsEmailValid(validateEmailAddresses(emailValue));
    } else {
      setIsEmailValid(true);
    }
  };

  // domain validation
  const isValidDomain = (domain: string) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain.trim());
  };

  const validateDomains = (domainString: string) => {
    const domains = domainString.split(',');
    return domains.every(isValidDomain);
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const domainValue = e.target.value;
    setOnlyDomain(domainValue);

    if (domainValue) {
      setIsDomainValid(validateDomains(domainValue));
    } else {
      setIsDomainValid(true);
    }
  };

  // for create / update authorisation form start
  const usePrevious = (value: any) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  const prevRetrieveAuthorisationProcess = usePrevious(
    retrieveAuthorisationProcess
  );

  // useEffect start
  useEffect(() => {
    const loadAuthorisationDetails = () => {
      setIsLoading(true);
      setFirstName('');
      setRadioValue('');
      const datePlusOneYear = new Date();
      datePlusOneYear.setFullYear(datePlusOneYear.getFullYear() + 1);
      setEndDate([datePlusOneYear]);
      setOnlyDomain('');
      setExternalId('');
      setChannel(null);
      setChannelFreeText('');
      setPrimaryContactEmail('');
      setPrimaryContactName('');
      setNote('');
      setIsBydaMember(true);
      setOwnerEmail('');
      setIsOwnerEmailValid(true);
      setOwnerEmailError('Invalid email');
      if (authorisationDetails && authorisationDetails.name) {
        setTimeout(() => {
          setFirstName(authorisationDetails.name);
          setIsLoading(false);
        }, 500);
        // setFirstName(authorisationDetails.name);
        // setIsLoading(false);
      }
      if (authorisationDetails && authorisationDetails.authorisationType) {
        const selectedItem = categoryListItems.find(
          (item) => item.value === authorisationDetails.authorisationType
        );
        if (selectedItem) {
          setAuthorisation(selectedItem);
        }
      }
      if (
        authorisationDetails?.domains &&
        authorisationDetails.domains.length === 0
      ) {
        setRadioValue('add-specific-users');
        setOnlyEmailAddress(authorisationDetails?.users?.join(', ') || '');
      } else {
        setRadioValue('match-email-domains');
        setOnlyDomain(authorisationDetails?.domains?.join(', ') || '');
      }

      if (authorisationDetails?.externalId) {
        setExternalId(authorisationDetails.externalId);
      } else {
        setIsBydaMember(false);
      }

      if (authorisationDetails?.channel) {
        if (
          authorisationDetails.channel === 'BYDA Direct' ||
          authorisationDetails.channel === 'Self Service' ||
          authorisationDetails.channel === 'Reseller (PelicanCorp)'
        ) {
          const channelOption = channelOptionItems.find(
            (c) => c.value === authorisationDetails.channel
          );
          setChannel(channelOption || null);
        } else {
          const channelOption = channelOptionItems.find(
            (c) => c.value === 'Other'
          );
          setChannel(channelOption || null);
          setChannelFreeText(authorisationDetails.channel);
        }
      }

      if (authorisationDetails?.primaryContactEmail) {
        setPrimaryContactEmail(authorisationDetails.primaryContactEmail);
      }

      if (authorisationDetails?.primaryContactName) {
        setPrimaryContactName(authorisationDetails.primaryContactName);
      }

      if (authorisationDetails?.note) {
        setNote(authorisationDetails.note);
      }

      // for suspended authorisation
      if (authorisationDetails?.deletedAt !== null) {
        setIsAllFormDisabled(!false);
      } else {
        setIsAllFormDisabled(false);
      }

      if (authorisationDetails?.ownerEmail) {
        setOwnerEmail(authorisationDetails.ownerEmail);
      }
    };

    // Handling form type specific logic
    switch (formType) {
      case 'add':
        setIsLoading(false);
        break;
      case 'add-order':
        setShowAddOrder(true);
        setShowAddOrderLink(false);
        setDisableEdit(true);
        break;
      case 'edit':
        setShowAddOrderLink(true);
        setDisableEdit(true);
        setIsLoading(true);
        setFirstName('');
        break;
      default:
        setShowAddOrder(false);
        setDisableEdit(false);
    }

    // Loading authorisation details
    if (formType === 'add-order' || formType === 'edit') {
      loadAuthorisationDetails();
    }
  }, [formType, authorisationDetails, categoryListItems]);

  useEffect(() => {
    // Resetting form fields when adding is done
    if (
      prevRetrieveAuthorisationProcess === 'adding' &&
      !retrieveAuthorisationProcess
    ) {
      resetFormFields();
    }
  }, [prevRetrieveAuthorisationProcess, retrieveAuthorisationProcess]);

  // Helper function to reset form fields
  const resetFormFields = () => {
    setFirstName('');
    setAuthorisation(null);
    setExternalId('');
    setRadioValue('');
    setOnlyEmailAddress('');
    setOnlyDomain('');
    setTransactions(0);
    setEndDate([]);
    setOrderReferenceCode('');
    setChannel(null);
    setChannelFreeText('');
    setPrimaryContactEmail('');
    setPrimaryContactName('');
    setOwnerEmail('');
    setIsOwnerEmailValid(true);
    setNote('');
    setIsBydaMember(true);
    setIsMembershipAllowance(false);
    setOrderNote('');
  };

  // Helper function to load authorisation details
  useEffect(() => {
    handleErrors(retrieveAuthorisationError);

    if (
      retrieveAuthorisationProcess === 'updatingFail' ||
      retrieveAuthorisationProcess === 'addingFail'
    ) {
      setIsUpdatingAuthorisation(false);
      setIsCreatingAuthorisation(false);
    }

    if (
      retrieveAuthorisationProcess === 'updatingDone' ||
      retrieveAuthorisationProcess === 'addingDone'
    ) {
      setIsUpdatingAuthorisation(false);
      setIsCreatingAuthorisation(false);
      onClose();
    }

    if (retrieveAuthorisationProcess === 'clearField') {
      setclearField(true);
    } else {
      setclearField(false);
    }
  }, [retrieveAuthorisationProcess, retrieveAuthorisationError, onClose]);

  const handleErrors = (error: any) => {
    switch (error) {
      case 'Users not found':
        setIsCreatingAuthorisation(false);
        setErrorAddSpecificUsers(false);
        break;
      case 'DUPLICATE_AUTHORISATION_NAME':
        setErrorMatchEmailDomains(false);
        break;
      case 'Event object failed validation':
      case 'DUPLICATE_AUTHORISATION_DOMAINS':
        setErrorMatchEmailDomains(true);
        break;
      case 'DUPLICATE_AUTHORISATION_USERS':
        setErrorAddSpecificUsers(true);
        break;
      case 'Unable to update authorisation':
        setIsUpdatingAuthorisation(false);
        break;
      case 'OWNER_NOT_REGISTERED':
        setOwnerEmailError(
          'This email address is not associated with a registered user in BYDA'
        );
        setIsOwnerEmailValid(false);
        break;
      case 'DUPLICATE_AUTHORISATION_USER_ID':
        setOwnerEmailError(
          'This email address already has premium features of the same type'
        );
        setIsOwnerEmailValid(false);
        break;
      default:
        setErrorAddSpecificUsers(false);
        setErrorMatchEmailDomains(false);
    }
  };

  useEffect(() => {
    if (retrieveOrderProcessStatus !== 'adding') {
      resetOrderFields();
    }
    if (retrieveOrderProcessStatus === undefined) {
      setIsAddingOrder(false);
    }
  }, [retrieveOrderProcessStatus]);

  const resetOrderFields = () => {
    setTransactions(0);
    setEndDate([]);
    setOrderReferenceCode('');
    setIsMembershipAllowance(false);
    setOrderNote('');
  };

  useEffect(() => {
    const isProcessUpdatingOrAdding = [
      'updating',
      'updatingDone',
      'adding',
      'addingDone'
    ].includes(retrieveAuthorisationProcess || '');

    const isOrderDeleting = retrieveOrderProcessStatus === 'deleting';
    setIsAllFormDisabled(isProcessUpdatingOrAdding || isOrderDeleting);
  }, [retrieveAuthorisationProcess, retrieveOrderProcessStatus]);
  // useEffect end

  return (
    <div className="global-right-modal authorisation-form">
      <section
        className={`global-right-modal--wrapper ${
          showAddOrder ? 'height-overflow' : ''
        }`}
      >
        <section className="global-right-modal--top">
          <div className="global-right-modal--header">
            {formType === 'edit' || formType === 'add-order' ? (
              <div className="modal-form-main-heading">Edit authorisation</div>
            ) : (
              <div className="modal-form-main-heading">New authorisation</div>
            )}
          </div>
        </section>
        <hr className="section--divider" />
        <section className="add-user-form-item pos-relative">
          {isLoading ? (
            <TextInputSkeleton />
          ) : (
            <TextInput
              id="firstName"
              className={`textName ${
                retrieveAuthorisationError === 'DUPLICATE_AUTHORISATION_NAME'
                  ? 'error-border'
                  : ''
              }`}
              name="firstName"
              labelText="Name"
              data-testid="firstName"
              maxLength={100}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={
                isDisableFormAfterCreate
                  ? false
                  : isAllFormDisabled || showAddOrder
              }
            />
          )}
          {!clearField &&
          retrieveAuthorisationError === 'DUPLICATE_AUTHORISATION_NAME' ? (
            <>
              <WarningFilled16 className="warning-icon" />
              <FormError>Duplicate authorisation name</FormError>
            </>
          ) : (
            <p
              className={`text-helper bx--label ${
                isAllFormDisabled || showAddOrder ? 'bx--label--disabled' : ''
              }`}
            >
              Authorisation name to display in your list
            </p>
          )}
        </section>
        <AuthorisationFormItem isLoading={isLoading}>
          <Dropdown
            id="channel"
            className="textAuthorisation"
            titleText="Channel"
            label="Choose an option"
            items={channelOptionItems}
            itemToString={(item: any) => item.label}
            onChange={({ selectedItem }) => {
              setChannel(selectedItem);
            }}
            selectedItem={channel}
            disabled={
              isDisableFormAfterCreate
                ? false
                : isAllFormDisabled || showAddOrder
            }
          />
        </AuthorisationFormItem>
        {channel?.value === 'Other' && (
          <AuthorisationFormItem isLoading={isLoading}>
            <TextInput
              id="channelFreeText"
              className="textBydaId"
              name="channelFreeText"
              labelText="Please specify"
              data-testid="channelFreeText"
              value={channelFreeText}
              onChange={(e) => setChannelFreeText(e.target.value || '')}
              disabled={
                isDisableFormAfterCreate
                  ? false
                  : isAllFormDisabled || showAddOrder
              }
              maxLength={50}
            />
          </AuthorisationFormItem>
        )}
        <AuthorisationFormItem isLoading={isLoading}>
          <fieldset className="bx--fieldset mb-0">
            <legend
              className={`textRadioGroup bx--label ${
                isAllFormDisabled || showAddOrder ? 'bx--label--disabled' : ''
              }`}
            >
              BYDA member
            </legend>

            <RadioButtonGroup
              name="isBydaMember "
              onChange={(val: string) => setIsBydaMember(val === 'yes')}
              disabled={
                isDisableFormAfterCreate
                  ? false
                  : isAllFormDisabled || showAddOrder
              }
              valueSelected={isBydaMember ? 'yes' : 'no'}
            >
              <RadioButton
                id="is-byda-member-yes"
                className="text-radio-button"
                labelText="Yes"
                value="yes"
              />
              <RadioButton
                id="is-byda-member-no"
                className="text-radio-button"
                labelText="No"
                value="no"
              />
            </RadioButtonGroup>
          </fieldset>
        </AuthorisationFormItem>
        {isBydaMember && (
          <AuthorisationFormItem isLoading={isLoading}>
            <TextInput
              id="externalId"
              className="textBydaId"
              name="externalId"
              labelText="BYDA ID"
              data-testid="externalId"
              value={externalId}
              onChange={(e) => handleExternalIdChange(e)}
              disabled={
                isDisableFormAfterCreate
                  ? false
                  : isAllFormDisabled || showAddOrder
              }
              maxLength={9}
            />
          </AuthorisationFormItem>
        )}
        <AuthorisationFormItem isLoading={isLoading}>
          <TextInput
            id="primaryContactName"
            className="textBydaId"
            name="primaryContactName"
            labelText="Primary contact name"
            data-testid="primaryContactName"
            value={primaryContactName}
            onChange={(e) => setPrimaryContactName(e.target.value || '')}
            disabled={
              isDisableFormAfterCreate
                ? false
                : isAllFormDisabled || showAddOrder
            }
            maxLength={200}
          />
        </AuthorisationFormItem>
        <AuthorisationFormItem isLoading={isLoading}>
          <TextInput
            id="primaryContactEmail"
            className="textBydaId"
            name="primaryContactEmail"
            labelText="Primary contact email address"
            data-testid="primaryContactEmail"
            value={primaryContactEmail}
            onChange={(e) => {
              setIsPrimaryContactEmailValid(isValidEmail(e.target.value));
              setPrimaryContactEmail(e.target.value || '');
            }}
            disabled={
              isDisableFormAfterCreate
                ? false
                : isAllFormDisabled || showAddOrder
            }
            maxLength={100}
          />
          {!isPrimaryContactEmailValid && primaryContactEmail && (
            <FormError>Invalid email</FormError>
          )}
        </AuthorisationFormItem>
        <hr className="section--divider" />
        <AuthorisationFormItem isLoading={isLoading}>
          <TextInput
            id="ownerEmail"
            className="textBydaId"
            name="ownerEmail"
            labelText="Transactions owner"
            data-testid="ownerEmail"
            value={ownerEmail}
            onChange={(e) => {
              setIsOwnerEmailValid(isValidEmail(e.target.value));
              setOwnerEmailError('Invalid email');
              setOwnerEmail(e.target.value || '');
            }}
            disabled={
              isDisableFormAfterCreate
                ? false
                : isAllFormDisabled || showAddOrder
            }
            maxLength={100}
          />
          {!isOwnerEmailValid && <FormError>{ownerEmailError}</FormError>}
        </AuthorisationFormItem>
        <hr className="section--divider" />
        <AuthorisationFormItem isLoading={isLoading}>
          <Dropdown
            id="authorisation"
            className="textAuthorisation"
            titleText="Authorisation"
            label="Select feature"
            items={categoryListItems}
            itemToString={(item: any) => item.label}
            onChange={({ selectedItem }) => {
              setAuthorisation(selectedItem);
              const datePlusOneYear = new Date();
              datePlusOneYear.setFullYear(datePlusOneYear.getFullYear() + 1);
              setEndDate([datePlusOneYear]);
            }}
            selectedItem={authorisation}
            disabled={
              isDisableFormAfterCreate
                ? false
                : isAllFormDisabled || disableEdit
            }
          />
        </AuthorisationFormItem>
        <section className="add-user-form-item radio-wrapper">
          {isLoading ? (
            <TextInputSkeleton />
          ) : (
            <fieldset className="bx--fieldset mb-0">
              <legend
                className={`textRadioGroup bx--label ${
                  isAllFormDisabled || showAddOrder ? 'bx--label--disabled' : ''
                }`}
              >
                Authorise users or groups
              </legend>

              <RadioButtonGroup
                name="appAccessible"
                onChange={(val: string) => {
                  setRadioValue(val);
                  if (val === 'add-specific-users') {
                    setOnlyDomain('');
                  } else if (val === 'match-email-domains') {
                    setOnlyEmailAddress('');
                  }
                  handleRadioChange();
                }}
                disabled={
                  isDisableFormAfterCreate
                    ? false
                    : isAllFormDisabled || showAddOrder
                }
                valueSelected={radioValue}
              >
                <RadioButton
                  id="radio-add-specific-users"
                  className="text-radio-button"
                  labelText="Add specific users"
                  value="add-specific-users"
                />
                <RadioButton
                  id="radio-match-email-domains"
                  className="text-radio-button"
                  labelText="Match email domains"
                  value="match-email-domains"
                />
              </RadioButtonGroup>
            </fieldset>
          )}
        </section>
        <section className="add-user-form-item">
          {isLoading || !radioValue ? (
            <TextInputSkeleton />
          ) : (
            <>
              {radioValue === 'add-specific-users' && (
                <div className="textarea-wrapper">
                  <TextArea
                    id="emailAddreses"
                    name="emailAddreses"
                    className={
                      !clearField && errorAddSpecificUsers ? 'error-border' : ''
                    }
                    labelText="Add specific user(s)"
                    data-testid="emailAddreses"
                    placeholder="email addresses"
                    value={onlyEmailAddress}
                    onChange={handleEmailChange}
                    disabled={
                      isDisableFormAfterCreate
                        ? false
                        : isAllFormDisabled || showAddOrder
                    }
                  />
                  {!clearField && errorAddSpecificUsers && (
                    <WarningFilled16 className="warning-icon" />
                  )}
                  {!clearField &&
                  retrieveAuthorisationError === 'Users not found' ? (
                    <FormError>One or more emails were not found</FormError>
                  ) : !clearField &&
                    retrieveAuthorisationError ===
                      'DUPLICATE_AUTHORISATION_USERS' ? (
                    <FormError>Duplicate authorisation user</FormError>
                  ) : !clearField &&
                    retrieveAuthorisationError ===
                      'Unable to update authorisation' ? (
                    <FormError>One or more emails were not found</FormError>
                  ) : !isEmailValid ? (
                    <FormError>Invalid email</FormError>
                  ) : (
                    <p
                      className={`text-helper bx--label ${
                        isAllFormDisabled ||
                        !isRadioButtonGroupEnabled ||
                        showAddOrder
                          ? 'bx--label--disabled'
                          : ''
                      }`}
                    >
                      Use commas to separate multiples
                    </p>
                  )}
                </div>
              )}
              {radioValue === 'match-email-domains' && (
                <div className="textarea-wrapper">
                  <TextInput
                    id="domains"
                    name="domains"
                    className={
                      !clearField && errorMatchEmailDomains
                        ? 'error-border'
                        : ''
                    }
                    labelText="Any email matching domain(s)"
                    data-testid="domains"
                    placeholder="domains"
                    value={onlyDomain}
                    onChange={handleDomainChange}
                    disabled={
                      isDisableFormAfterCreate
                        ? false
                        : isAllFormDisabled || showAddOrder
                    }
                  />
                  {!clearField &&
                    retrieveAuthorisationError &&
                    retrieveAuthorisationError !==
                      'DUPLICATE_AUTHORISATION_NAME' && (
                      <WarningFilled16 className="warning-icon" />
                    )}
                  {clearField &&
                  retrieveAuthorisationError ===
                    'Event object failed validation' ? (
                    <FormError>Event object failed validation</FormError>
                  ) : !clearField &&
                    retrieveAuthorisationError ===
                      'DUPLICATE_AUTHORISATION_DOMAINS' ? (
                    <FormError>One or more domains were taken</FormError>
                  ) : !isDomainValid ? (
                    <FormError>Invalid domain</FormError>
                  ) : (
                    <p
                      className={`text-helper bx--label ${
                        isAllFormDisabled ||
                        !isRadioButtonGroupEnabled ||
                        showAddOrder
                          ? 'bx--label--disabled'
                          : ''
                      }`}
                    >
                      Example: company.com, organisation.com
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>
        {!isLoading && (
          <section className="add-user-form-item">
            <div className="textarea-wrapper">
              <TextArea
                id="note"
                name="note"
                labelText="Notes (optional)"
                data-testid="note"
                value={note}
                onChange={(e) => setNote(e.target.value || '')}
                maxLength={500}
                disabled={
                  isDisableFormAfterCreate
                    ? false
                    : isAllFormDisabled || showAddOrder
                }
              />
            </div>
          </section>
        )}
        {isLoading ? (
          <section className="add-user-form-item">
            <TextInputSkeleton />
          </section>
        ) : (
          <>
            {formType === 'edit' && (
              <section className="add-user-form-item">
                <Link
                  className="cursor-pointer text-suspend"
                  onClick={() => {
                    if (!isAllFormDisabled) {
                      handleSuspendClick();
                    }
                  }}
                  disabled={
                    isDisableFormAfterCreate
                      ? false
                      : isAllFormDisabled || showAddOrder
                  }
                >
                  Suspend
                </Link>
              </section>
            )}
          </>
        )}
        <hr className="section--divider" />
        {isLoading ? (
          <section className="add-user-form-item">
            <TextInputSkeleton />
          </section>
        ) : (
          <section className="add-user-form-item flex-space-between">
            <h1 className="administrator-form-main-heading text-order">
              {formType === 'edit' ? (
                <div>Order history</div>
              ) : formType === 'add-order' ? (
                <div>Add order</div>
              ) : (
                <div>Order</div>
              )}
            </h1>
            {(formType === 'edit' || formType === 'add-order') &&
              showAddOrderLink && (
                <Link
                  className="cursor-pointer text-add-order"
                  onClick={() => {
                    if (!isAllFormDisabled) {
                      setShowAddOrder(true);
                      setShowAddOrderLink(false);
                      setFormType('add-order');
                      setEditCloseButton(true);
                    }
                  }}
                  disabled={
                    isDisableFormAfterCreate ? false : isAllFormDisabled
                  }
                >
                  Add order <Add16 />
                </Link>
              )}
          </section>
        )}
        {isLoading ? (
          <section className="add-user-form-item">
            <TextInputSkeleton />
          </section>
        ) : (
          <>
            {(formType === 'edit' || formType === 'add-order') &&
            !showAddOrder ? (
              <>
                <section
                  className={`add-user-form-item ${
                    isAllFormDisabled ? 'bx--label--disabled' : ''
                  }`}
                >
                  {authorisationDetails?.orders
                    ?.slice(
                      0,
                      showAllOrders ? authorisationDetails?.orders?.length : 3
                    )
                    .map((order, index) => (
                      <div key={index} className="order-history-wrapper">
                        <div>
                          <span className="text-date">
                            {formatDateToDDMMYYYY(order.createdAt)}
                          </span>
                          <span className="text-reference">
                            ({order.reference})
                          </span>
                          {Number(order.transactionCount) === order.amount ||
                          new Date(order.expiredAt) <= new Date() ? (
                            ''
                          ) : (
                            <Link
                              className="delete cursor-pointer text-delete"
                              onClick={() => {
                                if (!isAllFormDisabled) {
                                  setAuthorisationIdToDelete(
                                    authorisationId || null
                                  );
                                  setOrderIdToDelete(order.id || null);
                                  setShowDeleteToast(true);
                                  setShowToast(false);
                                }
                              }}
                              disabled={isAllFormDisabled}
                            >
                              Delete
                            </Link>
                          )}
                        </div>
                        <div>
                          {Number(order.transactionCount) === order.amount ? (
                            <span className="text-transactions">
                              Used all {order.amount},
                            </span>
                          ) : (
                            <span className="text-transactions">
                              Used {order.transactionCount}/{order.amount},
                            </span>
                          )}
                          <span className="text-expiry-date">
                            Expiry {formatDateToDDMMYYYY(order.expiredAt)}
                          </span>
                        </div>
                        <div className="order-note">{order.note}</div>
                      </div>
                    ))}
                </section>
                {(authorisationDetails?.orders?.length || 0) > 3 &&
                  !showAllOrders && (
                    <section className="add-user-form-item">
                      <Link
                        className="text-show-all"
                        onClick={() => setShowAllOrders(true)}
                        disabled={
                          isDisableFormAfterCreate ? false : isAllFormDisabled
                        }
                      >
                        Show all
                      </Link>
                    </section>
                  )}
              </>
            ) : (
              <>
                <section className="add-user-form-item membership-allowance">
                  <Checkbox
                    labelText="BYDA membership allowance"
                    id="is-membership-allowance"
                    checked={isMembershipAllowance}
                    disabled={
                      isDisableFormAfterCreate
                        ? false
                        : isAllFormDisabled ||
                          isOrderFormDisabled ||
                          !authorisation
                    }
                    onChange={(value: boolean) =>
                      setIsMembershipAllowance(value)
                    }
                  />
                </section>
                <section className="add-user-form-item datePicker--wrapper">
                  <div className="form-transactions">
                    <div className="max-area-input-container">
                      <NumberInput
                        id="max-area-input"
                        label="Transactions"
                        max={MAX_AREA_MAX}
                        min={MAX_AREA_MIN}
                        step={MAX_AREA_STEP}
                        invalidText="Maximum value allowed is 500,000"
                        value={transactions !== null ? transactions : 0}
                        onChange={handleNumberInputChange}
                        disabled={
                          isDisableFormAfterCreate
                            ? false
                            : isAllFormDisabled ||
                              isOrderFormDisabled ||
                              !authorisation
                        }
                      />
                    </div>
                  </div>
                  <div className="form-end-date">
                    <DatePicker
                      dateFormat="d/m/Y"
                      minDate={minDate}
                      datePickerType="single"
                      value={endDate}
                      onChange={handleDatePickerChange}
                    >
                      <DatePickerInput
                        id="digEndAt"
                        name="digEndAt"
                        labelText="End date"
                        data-testid="digEndAt"
                        placeholder="dd/mm/yyyy"
                        type="text"
                        autoComplete="off"
                        disabled={
                          isDisableFormAfterCreate
                            ? false
                            : isAllFormDisabled ||
                              isOrderFormDisabled ||
                              !authorisation
                        }
                      />
                    </DatePicker>
                  </div>
                </section>
                <section className="add-user-form-item">
                  <TextInput
                    id="orderReferenceCode"
                    name="orderReferenceCode"
                    labelText="Your order reference code"
                    value={orderReferenceCode}
                    onChange={(e) => handleOrderReferenceCodeChange(e)}
                    disabled={
                      isDisableFormAfterCreate
                        ? false
                        : isAllFormDisabled ||
                          isOrderFormDisabled ||
                          !authorisation
                    }
                    maxLength={100}
                    data-testid="orderReferenceCode"
                  />
                  <div className="textarea-wrapper order-note">
                    <TextArea
                      id="orderNote"
                      name="orderNote"
                      labelText="Notes (optional)"
                      data-testid="orderNote"
                      value={orderNote}
                      maxLength={500}
                      onChange={(e) => setOrderNote(e.target.value || '')}
                      disabled={
                        isDisableFormAfterCreate
                          ? false
                          : isAllFormDisabled ||
                            isOrderFormDisabled ||
                            !authorisation
                      }
                    />
                  </div>
                  {showAddOrder && (
                    <div className="add-order-links">
                      <Link
                        className="no-link cursor-pointer"
                        disabled={
                          isDisableFormAfterCreate ? false : isOrderFormDisabled
                        }
                        onClick={() => {
                          if (!isOrderFormDisabled) {
                            setShowAddOrder(false);
                            setShowAddOrderLink(true);
                            setFormType('edit');
                          }
                        }}
                      >
                        Cancel
                      </Link>
                      <Link
                        className="cursor-pointer"
                        disabled={
                          isDisableFormAfterCreate
                            ? false
                            : isOrderFormDisabled || !validationForAddOrder
                        }
                        onClick={() => {
                          if (!isOrderFormDisabled) {
                            if (validationForAddOrder) {
                              handleAddOrder();
                              setTimeout(() => {
                                setShowAddOrder(false);
                                setShowAddOrderLink(true);
                                // setEditCloseButton(true);
                                setFormType('edit');
                              }, 1500);
                            }
                          }
                        }}
                      >
                        {isAddingOrder ? (
                          <>
                            <Loading small withOverlay={false} /> Adding ...
                          </>
                        ) : (
                          'Add'
                        )}
                      </Link>
                    </div>
                  )}
                </section>
              </>
            )}
          </>
        )}

        {(retrieveAuthorisationError === 'Unable to get authorisations' ||
          retrieveAuthorisationError === 'AUTHORISATION_DOES_NOT_EXIST') && (
          <section className="add-user-form-item">
            <FormError>Sorry, this authorisation has been suspended.</FormError>
          </section>
        )}
        <div className="button-set button-wrapper">
          <Button
            id="cancel"
            className="text-large-button"
            name="cancel"
            kind="secondary"
            data-testid="cancel"
            onClick={handleCancel}
            disabled={
              editCloseButton === false
                ? false
                : isDisableFormAfterCreate
                ? false
                : isAllFormDisabled || showAddOrder
            }
          >
            Cancel
          </Button>
          {showAddOrder ? (
            <Button
              id="save"
              className="text-large-button"
              name="save"
              type="submit"
              kind="primary"
              data-testid="submit"
              disabled={isDisableFormAfterCreate ? false : true}
            >
              Save
            </Button>
          ) : formType === 'edit' ? (
            <Button
              id="edit"
              className="text-large-button"
              name="edit"
              type="submit"
              kind="primary"
              data-testid="submit"
              disabled={
                isDisableFormAfterCreate
                  ? false
                  : isAllFormDisabled ||
                    !validationForEdit ||
                    !isEmailValid ||
                    !isPrimaryContactEmailValid ||
                    !isDomainValid ||
                    isUpdatingAuthorisation
              }
              onClick={() => {
                handleUpdateAuthorisation();
              }}
            >
              {isUpdatingAuthorisation ? (
                <>
                  <Loading small withOverlay={false} /> Saving ...
                </>
              ) : (
                'Save'
              )}
            </Button>
          ) : (
            <Button
              id="create"
              className="text-large-button"
              name="create"
              type="submit"
              kind="primary"
              data-testid="submit"
              disabled={
                isDisableFormAfterCreate
                  ? false
                  : !validationForCreate ||
                    !isEmailValid ||
                    !isPrimaryContactEmailValid ||
                    !isDomainValid ||
                    isCreatingAuthorisation
              }
              onClick={() => {
                handleSubmit();
              }}
            >
              {isCreatingAuthorisation ? (
                <>
                  <Loading small withOverlay={false} /> Creating ...
                </>
              ) : (
                'Create'
              )}
            </Button>
          )}
        </div>
      </section>

      {showToast && (
        <ExtendedToastNotification
          kind={'warning'}
          title={`Confirm action`}
          iconDescription={'warning'}
          caption={`Are you sure you want to suspend this authorisation?`}
          buttonText="Yes, suspend"
          onButtonClick={() => {
            confirmSuspend();
          }}
          onClose={() => setShowToast(false)}
        />
      )}
      {showDeleteToast && (
        <ExtendedToastNotification
          kind={'error'}
          title={`Confirm action`}
          caption={`Are you sure you want to delete this order?`}
          buttonText="Yes, delete"
          onButtonClick={() => {
            if (authorisationIdToDelete !== null && orderIdToDelete !== null) {
              handleDeleteOrder(authorisationIdToDelete, orderIdToDelete);
              setShowDeleteToast(false);
            }
          }}
          onClose={() => setShowDeleteToast(false)}
        />
      )}
    </div>
  );
};
export default AuthorisationForm;
